import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Control from "../../../../common/Control";
import ExternalValue from "./ExternalValue";
import CalculatedValue from "./CalculatedValue";
import { DateTime } from "luxon";

const EditPopover = ({
  isOpen,
  onClose,
  onSave,
  field,
  fieldLabel,
  fieldType,
  fieldOptions,
  fieldProps,
  currentValue,
  buttonRef,
  externalValue,
  calculatedValue,
}) => {
  const [value, setValue] = useState(currentValue || "");
  const popoverRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setValue(currentValue || "");
      // Donner le focus à l'input après un petit délai pour s'assurer que le DOM est rendu
      setTimeout(() => {
        if (inputRef.current) {
          const input = inputRef.current.querySelector(
            "input, select, textarea",
          );
          if (input) {
            input.focus();
            // Pour les inputs text/number, sélectionner tout le contenu
            if (input.type === "text" || input.type === "number") {
              input.select();
            }
          }
        }
      }, 100);
    }
  }, [isOpen, currentValue]);

  // Fermer le popover en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, onClose, buttonRef]);

  const handleSave = () => {
    let processedValue = value;

    // Traitement selon le type de champ
    if (fieldType === "number") {
      processedValue = parseFloat(value) || 0;
    }

    onSave(processedValue);
    onClose();
  };

  const handleChange = (e) => {
    setValue(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  const handleApplyExternalValue = () => {
    setValue(externalValue);
  };

  const handleApplyCalculatedValue = () => {
    setValue(calculatedValue);
  };

  // Préparer les données pour le composant Control
  const getControlProps = () => {
    const baseProps = {
      label: fieldLabel,
      type: fieldType,
      value: value,
      change: handleChange,
      name: "simulatorField",
      className: "mb-2 text-dark",
      ...fieldProps,
    };

    // Pour les selects, préparer les options au format attendu par Control
    if (fieldType === "select" && fieldOptions) {
      baseProps.datas = fieldOptions.map((option) => ({
        id: option,
        name: option,
      }));
    }

    if (fieldType === "date") {
      //check if the value is a valid date, if not, dont format it
      if (DateTime.fromISO(value).isValid) {
        baseProps.value = DateTime.fromISO(value).toFormat("yyyy-MM-dd");
      }
    }

    return baseProps;
  };

  // Calcul de la position du popover
  const getPopoverStyle = () => {
    if (!buttonRef.current) return {};

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const popoverHeight = 200; // Estimation de la hauteur du popover

    let top = buttonRect.top - popoverHeight - 10; // Au-dessus du bouton
    let left = buttonRect.left;

    // Ajustements si le popover sort de l'écran
    if (top < 10) {
      top = buttonRect.bottom + 10; // En dessous si pas de place au-dessus
    }

    if (left + 350 > window.innerWidth) {
      left = window.innerWidth - 360; // Ajuster si déborde à droite
    }

    if (left < 10) {
      left = 10; // Minimum de marge à gauche
    }

    return {
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 1050,
      minWidth: "320px",
      maxWidth: "400px",
      height: popoverHeight,
      background: "#ffffff75",
      backdropFilter: "blur(6px)",
      borderRadius: 20,
    };
  };

  if (!isOpen) return null;

  const popoverContent = (
    <div
      ref={popoverRef}
      className=" border  shadow-lg p-3 d-flex justify-content-end flex-column align-items-start rounded "
      style={getPopoverStyle()}
      onKeyDown={handleKeyPress}
    >
      {/* Flèche pointant vers le bouton */}
      <div
        className="position-absolute"
        style={{
          bottom: "-8px",
          left: "20px",
          width: 0,
          height: 0,
          borderLeft: "8px solid transparent",
          borderRight: "8px solid transparent",
          borderTop: "8px solid white",
        }}
      />
      <div
        className="position-absolute"
        style={{
          bottom: "-9px",
          left: "19px",
          width: 0,
          height: 0,
          borderLeft: "9px solid transparent",
          borderRight: "9px solid transparent",
          borderTop: "9px solid #dee2e6",
        }}
      />

      {externalValue && (
        <div className="mb-1">
          <ExternalValue
            field={field}
            value={externalValue}
            onApply={handleApplyExternalValue}
          />
        </div>
      )}
      {calculatedValue && (
        <div className="mb-1">
          <CalculatedValue
            field={field}
            value={calculatedValue}
            onApply={handleApplyCalculatedValue}
          />
        </div>
      )}
      <div className="mb-1" ref={inputRef}>
        <Control {...getControlProps()} />
      </div>

      <div
        className="d-flex align-items-center"
        style={{
          gap: 10,
        }}
      >
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={handleSave}
        >
          Valider
        </button>
        <button
          type="button"
          className="btn btn-sm btn-default"
          onClick={onClose}
        >
          Annuler
        </button>
      </div>
    </div>
  );

  // Utiliser createPortal pour rendre en dehors de la hiérarchie du tableau
  return createPortal(popoverContent, document.body);
};

export default EditPopover;
